import type { Note, NoteMedia } from '@monolog/shared/types/database/metadata-svc';
import { default as GatewayBase } from './gateway-base';
import type { User } from '@monolog/shared/types/database/identity-svc';
import type {
  CustomTagRuleDTO,
  CustomTagRuleTestResult,
  RuleCondition,
  SampleClaimSummary,
} from '@monolog/shared/types';
import type { CustomTagTriggerId, SubjectType, TagScope } from '@constants';

export type Label = {
  id: string;
  value: string;
  accountId: string;
  source?: string;
};
export type TagAction = {
  action: 'add' | 'remove';
  storeId: string;
  subjectId: string;
  tagId: string;
  userDisplayName?: string;
  userId?: string;
};

export type CreateNoteInput = Partial<Omit<Note, 'media'> & { media: { fileKey: string }[]; subjectType: SubjectType }>;

export type GetNotesResponse = Note & { user: User | null; media: NoteMedia[] };

export default class MetadataService extends GatewayBase {
  get notes() {
    return {
      create: this.createNote,
      get: this.getNotes,
    };
  }

  get metadata() {
    return {
      add: this.addMetadata,
      get: this.getMetadata,
    };
  }

  get tags() {
    return {
      create: this.createTag,
      list: this.getTagsByAccountId,
      query: this.queryTagsByAccountId,
      subject: {
        add: this.addTagToSubject,
        get: this.getTagsBySubjectId,
        remove: this.removeTagFromSubject,
        update: this.updateTagsFromSubject,
      },
    };
  }

  // Custom Tag Rules (ENG-5607)
  get customTagRules() {
    return {
      create: this.createCustomTagRule,
      get: this.getCustomTagRule,
      list: this.listCustomTagRules,
      remove: this.removeCustomTagRule,
      sampleClaims: this.listSampleClaims,
      test: this.testCustomTagRule,
      update: this.updateCustomTagRule,
    };
  }

  private listCustomTagRules = (storeId: string) => {
    return this.client.get<CustomTagRuleDTO[]>(`v1/custom-tag-rules`, { params: { storeId } });
  };

  private getCustomTagRule = (ruleId: string, storeId: string) => {
    return this.client.get<CustomTagRuleDTO>(`v1/custom-tag-rules/${ruleId}`, { params: { storeId } });
  };

  private createCustomTagRule = (
    storeId: string,
    body: {
      name: string;
      scope: TagScope;
      triggerEvent: CustomTagTriggerId;
      conditions: RuleCondition[];
      templates: string[];
      enabled?: boolean;
    },
  ) => {
    return this.client.post<CustomTagRuleDTO>(`v1/custom-tag-rules`, body, { params: { storeId } });
  };

  private updateCustomTagRule = (
    ruleId: string,
    storeId: string,
    body: Partial<{
      name: string;
      scope: TagScope;
      triggerEvent: CustomTagTriggerId;
      conditions: RuleCondition[];
      templates: string[];
      enabled: boolean;
    }>,
  ) => {
    return this.client.put<CustomTagRuleDTO>(`v1/custom-tag-rules/${ruleId}`, body, { params: { storeId } });
  };

  private removeCustomTagRule = (ruleId: string, storeId: string) => {
    return this.client.delete(`v1/custom-tag-rules/${ruleId}`, { params: { storeId } });
  };

  private testCustomTagRule = (ruleId: string, storeId: string, body: { claimId?: string } = {}) => {
    return this.client.post<CustomTagRuleTestResult>(`v1/custom-tag-rules/${ruleId}/test`, body, {
      params: { storeId },
    });
  };

  private listSampleClaims = (params: { storeId: string; triggerEvent: CustomTagTriggerId }) => {
    return this.client.get<SampleClaimSummary[]>(`v1/custom-tag-rules/sample-claims`, { params });
  };

  private getTagsBySubjectId = (subjectId: string) => {
    return this.client.get<Label[]>(`v1/subjects/${subjectId}/labels`);
  };

  private addTagToSubject = (input: {
    subjectId: string;
    tagId: string;
    storeId: string;
    userDisplayName?: string;
    userId?: string;
  }) => {
    return this.client.post(`v1/subjects/${input.subjectId}/labels/${input.tagId}`, {
      storeId: input.storeId,
      userDisplayName: input.userDisplayName,
      userId: input.userId,
    });
  };

  private removeTagFromSubject = (subjectId: string, tagId: string) => {
    return this.client.delete(`v1/subjects/${subjectId}/labels/${tagId}`);
  };

  private updateTagsFromSubject = (tags: TagAction[], accountId: string) => {
    return this.client.put(`v1/labels/${accountId}`, tags);
  };

  private getNotes = (id: string) => {
    return this.client.get<GetNotesResponse[]>(`v1/notes/${id}`);
  };

  private createNote = (id: string, input: CreateNoteInput) => {
    return this.client.post(`v1/notes/${id}`, input);
  };

  private getMetadata = (subjectId: string) => {
    return this.client.get(`v1/metadata/${subjectId}`);
  };

  private addMetadata = (
    subjectId: string,
    input: { key: string; value: string; addedByUserId?: string; addedByUsername?: string },
  ) => {
    return this.client.post(`v1/metadata/${subjectId}`, input);
  };

  private queryTagsByAccountId = (accountId: string, query: string) => {
    return this.client.get<Label[]>(`v1/labels/${accountId}`, {
      params: {
        q: query,
      },
    });
  };

  private getTagsByAccountId = (accountId: string) => {
    return this.client.get<Label[]>(`v1/labels/${accountId}`);
  };

  private createTag = (accountId: string, input: { name: string; source?: string }) => {
    return this.client.post<Label>(`v1/labels/${accountId}`, input);
  };
}
